import {
  NewSelectionFilter,
  SelectionFilter,
  SelectionFilterType,
} from '@api/twin/SelectionFilterApi';
import { Button } from '@stereograph/teia-system-design/components/Button';
import { Checkbox } from '@stereograph/teia-system-design/components/Checkbox';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '@stereograph/teia-system-design/components/Modal';
import { Stack } from '@stereograph/teia-system-design/components/Stack';
import { TextField } from '@stereograph/teia-system-design/components/TextField';
import { TeiaSearchFilter } from '@stereograph/teia-system-design/utils';
import { useTeiaViewerContext, useTwinSource } from '@stereograph/teiaviewer';
import { usePluginTranslation } from '@translation';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { usePostSelectionFilter } from '../hooks/usePostSelectionFilter';

interface TeiaSaveSelectionModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  filter?: TeiaSearchFilter;
}

export const TeiaSaveSelectionModal = (props: TeiaSaveSelectionModalProps) => {
  const { isModalOpen, onClose, filter } = props;
  const { t } = usePluginTranslation();
  const { viewer } = useTeiaViewerContext();
  const twin = useTwinSource();
  const { mutate: postSelectionFilter, isPending } = usePostSelectionFilter(twin.projectId);

  const methods = useForm<SelectionFilter>({
    defaultValues: {
      name: '',
      filter: undefined,
      isPublic: false,
    },
    mode: 'onChange',
  });

  const createStaticFilter = () => {
    const filter: TeiaSearchFilter = {
      condition: 'Or',
      rules: [],
    };

    const uuids = viewer.scene.objectSelection.getUuids();
    for (const uuid of uuids) {
      filter.rules.push({
        type: 'Guid',
        operator: 'EqualTo',
        value: uuid,
      });
    }

    return filter;
  };

  const onCloseModal = () => {
    methods.reset();
    onClose();
  };

  const handleCreateSelection: SubmitHandler<SelectionFilter> = (data) => {
    if (isPending) {
      return;
    }
    const newFilter = filter ?? createStaticFilter();

    const newSelectionFilter: NewSelectionFilter = {
      name: data.name,
      type: filter ? SelectionFilterType.DYNAMIC : SelectionFilterType.STATIC,
      filter: newFilter,
      isPublic: data.isPublic,
    };

    postSelectionFilter(
      {
        selectionFilter: newSelectionFilter,
      },
      {
        onSuccess: onCloseModal,
      },
    );
  };

  return (
    <Modal
      id="new-selection-modal"
      open={isModalOpen}
      onClose={onCloseModal}
    >
      <form onSubmit={methods.handleSubmit(handleCreateSelection)}>
        <ModalHeader>
          {t('selection_plugin.record_widget.selection_save.modal.title')}
        </ModalHeader>
        <ModalContent>
          <Stack
            spacing={1}
            sx={{
              mt: 2,
            }}
          >
            <Controller
              render={({ field }) => (
                <TextField
                  label={t('selection_plugin.record_widget.selection_save.modal.name.label')}
                  placeholder={t(
                    'selection_plugin.record_widget.selection_save.modal.name.placeholder',
                  )}
                  required
                  {...field}
                  data-dd-action-name="tvp.selection.create_form.name"
                />
              )}
              name="name"
              control={methods.control}
              rules={{
                required: true,
                min: 1,
              }}
            />
            <Controller
              name="isPublic"
              control={methods.control}
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  label={t('selection_plugin.record_widget.selection_save.modal.visibility.label')}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
        </ModalContent>
        <ModalFooter>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={3}
          >
            <Button
              color="info"
              type="button"
              onClick={onCloseModal}
              data-dd-action-name="tvp.selection.create_form.cancel"
            >
              {t('selection_plugin.record_widget.selection_save.modal.cancel_button.label')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!methods.formState.isValid}
              isLoading={isPending}
              sx={{
                minWidth: 100,
              }}
              data-dd-action-name="tvp.selection.create_form.submit"
            >
              {t('selection_plugin.record_widget.selection_save.modal.validate_button.label')}
            </Button>
          </Stack>
        </ModalFooter>
      </form>
    </Modal>
  );
};
